import { useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { BarChart3, Camera, Clock, MapPin, Send } from "lucide-react";
import {
  Body,
  Card,
  CardTitle,
  LoadingState,
  PageHeader,
  Subtitle,
  VButton,
  VField,
  VInput,
  VKpiCard,
  cn,
} from "@/components/ui";
import { stripAssetTypePrefix } from "@/domains/assets";
import type { DateRange } from "@/domains/reporting/api/types";
import { useKpiData } from "@/domains/reporting/hooks/useKpiData";
import {
  ExportButton,
  RentedAssetExportButton,
} from "@/domains/reporting/components/exportButton";

const COLOR_PRIMARY = "#1e40af";
const COLOR_INFO = "#3b82f6";
const COLOR_SUCCESS = "#059669";
const COLOR_WARNING = "#d97706";
const COLOR_NEUTRAL = "#64748b";

const ASSET_TYPE_COLORS: ReadonlyArray<string> = [
  COLOR_PRIMARY,
  COLOR_SUCCESS,
  COLOR_WARNING,
  COLOR_INFO,
  COLOR_NEUTRAL,
];

const AXIS_TICK = { fontSize: 12, fill: "#64748b" };
const GRID_STROKE = "#e2e8f0";

const DEFAULT_RANGE: DateRange = { from: "2026-01-01", to: "2026-12-31" };

type RangePreset = "ytd" | "q2" | "thisMonth" | "year";

const PRESETS: Record<RangePreset, { label: string; range: DateRange }> = {
  ytd: { label: "Year-to-date", range: { from: "2026-01-01", to: "2026-05-11" } },
  thisMonth: { label: "Deze maand", range: { from: "2026-05-01", to: "2026-05-31" } },
  q2: { label: "Q2 2026", range: { from: "2026-04-01", to: "2026-06-30" } },
  year: { label: "Volledig 2026", range: DEFAULT_RANGE },
};

type KpiAccent = "primary" | "info" | "success" | "warning";

const ACCENT_CLASSES: Record<KpiAccent, string> = {
  primary: "bg-primary-100 text-primary-700",
  info: "bg-info-100 text-info-700",
  success: "bg-success-100 text-success-700",
  warning: "bg-warning-100 text-warning-700",
};

function KpiIcon({
  accent,
  children,
}: {
  accent: KpiAccent;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <span
      className={`inline-flex size-8 items-center justify-center rounded-md ${ACCENT_CLASSES[accent]}`}
    >
      {children}
    </span>
  );
}

export function ReportingPage(): React.JSX.Element {
  const [range, setRange] = useState<DateRange>(DEFAULT_RANGE);
  const data = useKpiData(range);

  if (data.isPending) {
    return <LoadingState label="KPI-data berekenen…" />;
  }

  const handlePreset = (preset: RangePreset): void => {
    setRange(PRESETS[preset].range);
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Rapportage"
        subtitle="KPI's, gebruik per type asset en exportmogelijkheden voor interne rapportage."
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <RentedAssetExportButton rows={data.currentlyRented} />
            <ExportButton rows={data.reportRows} />
          </div>
        }
      />

      <Card padding="md">
        <div className="flex flex-wrap items-end gap-3">
          <div className="w-44">
            <VField label="Periode vanaf" htmlFor="rangeFrom">
              <VInput
                id="rangeFrom"
                type="date"
                value={range.from}
                onChange={(e) => setRange((r) => ({ ...r, from: e.target.value }))}
              />
            </VField>
          </div>
          <div className="w-44">
            <VField label="Periode tot" htmlFor="rangeTo">
              <VInput
                id="rangeTo"
                type="date"
                value={range.to}
                onChange={(e) => setRange((r) => ({ ...r, to: e.target.value }))}
              />
            </VField>
          </div>
          <div className="flex flex-wrap gap-2">
            {(Object.keys(PRESETS) as Array<RangePreset>).map((p) => (
              <VButton
                key={p}
                variant="outline"
                size="sm"
                onClick={() => handlePreset(p)}
              >
                {PRESETS[p].label}
              </VButton>
            ))}
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
        <VKpiCard
          label="Gem. inzetduur"
          value={`${data.summary.averageProcessingDays} d`}
          helper="Gemiddeld aantal dagen per inzet"
          icon={
            <KpiIcon accent="primary">
              <Clock className="size-4" aria-hidden />
            </KpiIcon>
          }
        />
        <VKpiCard
          label="Responsetijd partners"
          value={`${data.summary.averageResponseHours} u`}
          helper="Gemiddeld antwoord plaatsingsfirma"
          icon={
            <KpiIcon accent="info">
              <Send className="size-4" aria-hidden />
            </KpiIcon>
          }
        />
        <VKpiCard
          label="Inzetten in periode"
          value={data.summary.totalDeployments}
          helper="Aantal dossiers binnen filter"
          icon={
            <KpiIcon accent="success">
              <BarChart3 className="size-4" aria-hidden />
            </KpiIcon>
          }
        />
        <VKpiCard
          label="Vandaag verhuurd"
          value={data.currentlyRented.length}
          helper="Assets actief op locatie"
          icon={
            <KpiIcon accent="warning">
              <Camera className="size-4" aria-hidden />
            </KpiIcon>
          }
        />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card padding="lg" className="space-y-3">
          <div className="space-y-1">
            <CardTitle>Gemiddelde verwerkingstijd per maand</CardTitle>
            <Subtitle>Aantal dagen tussen begin- en einddatum van inzet.</Subtitle>
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={[...data.processingByMonth]}
                margin={{ top: 8, right: 8, left: -16, bottom: 0 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke={GRID_STROKE} vertical={false} />
                <XAxis dataKey="month" tick={AXIS_TICK} tickLine={false} axisLine={false} />
                <YAxis tick={AXIS_TICK} tickLine={false} axisLine={false} />
                <Tooltip cursor={{ fill: "#f1f5f9" }} />
                <Legend wrapperStyle={{ fontSize: 12 }} iconType="circle" />
                <Bar dataKey="averageDays" name="Gem. dagen" fill={COLOR_PRIMARY} radius={[4, 4, 0, 0]} />
                <Bar dataKey="count" name="Aantal dossiers" fill={COLOR_INFO} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card padding="lg" className="space-y-3">
          <div className="space-y-1">
            <CardTitle>Gemiddelde tijd tussen taken</CardTitle>
            <Subtitle>Per taakcategorie (in uren).</Subtitle>
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={[...data.taskTiming]}
                layout="vertical"
                margin={{ top: 8, right: 16, left: 0, bottom: 0 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke={GRID_STROKE} horizontal={false} />
                <XAxis type="number" tick={AXIS_TICK} tickLine={false} axisLine={false} />
                <YAxis
                  dataKey="category"
                  type="category"
                  width={120}
                  tick={AXIS_TICK}
                  tickLine={false}
                  axisLine={false}
                />
                <Tooltip cursor={{ fill: "#f1f5f9" }} />
                <Bar dataKey="averageHours" fill={COLOR_SUCCESS} radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card padding="lg" className="space-y-3">
          <div className="space-y-1">
            <CardTitle>Inzet per type asset</CardTitle>
            <Subtitle>Verdeling van alle inzetten over asset-types.</Subtitle>
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={[...data.byType]}
                  dataKey="count"
                  nameKey="label"
                  innerRadius={55}
                  outerRadius={90}
                  paddingAngle={2}
                  strokeWidth={2}
                >
                  {data.byType.map((_, idx) => (
                    <Cell key={idx} fill={ASSET_TYPE_COLORS[idx % ASSET_TYPE_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend
                  wrapperStyle={{ fontSize: 12 }}
                  iconType="circle"
                  verticalAlign="bottom"
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card padding="lg" className="space-y-3">
          <div className="space-y-1">
            <CardTitle>Inzet per stadsdeel</CardTitle>
            <Subtitle>Aantal dossiers per stadsdeel.</Subtitle>
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={[...data.byDistrict]}
                layout="vertical"
                margin={{ top: 8, right: 16, left: 0, bottom: 0 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke={GRID_STROKE} horizontal={false} />
                <XAxis type="number" tick={AXIS_TICK} tickLine={false} axisLine={false} />
                <YAxis
                  dataKey="district"
                  type="category"
                  width={140}
                  tick={AXIS_TICK}
                  tickLine={false}
                  axisLine={false}
                />
                <Tooltip cursor={{ fill: "#f1f5f9" }} />
                <Bar dataKey="count" fill={COLOR_WARNING} radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>

      <Card padding="lg" className="space-y-3">
        <div className="space-y-1">
          <CardTitle>Asset-utilisatie</CardTitle>
          <Subtitle>
            Per asset het aantal inzetten en bezettingsgraad binnen de geselecteerde periode.
          </Subtitle>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/60 text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-3 py-2 text-left">Asset</th>
                <th className="px-3 py-2 text-left">Type</th>
                <th className="px-3 py-2 text-right">Inzetten</th>
                <th className="px-3 py-2 text-right">Dagen verhuurd</th>
                <th className="px-3 py-2 text-right">Gem. duur</th>
                <th className="px-3 py-2 text-right">Bezetting</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {data.utilization.map((row) => (
                <tr key={row.assetId} className="transition-colors hover:bg-muted/40">
                  <td className="px-3 py-2">
                    <Body weight="semibold">{row.number}</Body>
                    <span className="block text-xs text-muted-foreground">
                      {stripAssetTypePrefix(row.name, row.type)}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-muted-foreground">{row.typeLabel}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{row.deploymentCount}</td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {row.daysUsed} / {row.daysInRange}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {row.averageDaysPerDeployment} d
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    <UtilizationBar pct={row.utilizationPct} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Card padding="lg" className="space-y-3">
        <div className="flex items-center gap-2">
          <Camera className="size-4 text-muted-foreground" aria-hidden />
          <CardTitle>Vandaag verhuurd</CardTitle>
        </div>
        <Body tone="muted">
          Snapshot van alle assets die op vandaag fysiek verhuurd staan — exporteer dit via de
          knop bovenaan om eigen Excel-dashboards te maken.
        </Body>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/60 text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-3 py-2 text-left">Asset</th>
                <th className="px-3 py-2 text-left">Type</th>
                <th className="px-3 py-2 text-left">Dossier</th>
                <th className="px-3 py-2 text-left">Adres</th>
                <th className="px-3 py-2 text-right">Eind</th>
                <th className="px-3 py-2 text-right">Dagen resterend</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {data.currentlyRented.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-3 py-4 text-center text-muted-foreground">
                    Geen assets verhuurd vandaag.
                  </td>
                </tr>
              ) : (
                data.currentlyRented.map((row) => (
                  <tr
                    key={`${row.assetId}-${row.caseNumber}`}
                    className="transition-colors hover:bg-muted/40"
                  >
                    <td className="px-3 py-2 font-semibold">{row.number}</td>
                    <td className="px-3 py-2 text-muted-foreground">{row.typeLabel}</td>
                    <td className="px-3 py-2">
                      #{row.caseNumber}{" "}
                      <span className="text-muted-foreground">— {row.caseTitle}</span>
                    </td>
                    <td className="px-3 py-2 text-muted-foreground">{row.address}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{row.endDate}</td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {row.daysRemaining} d
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>

      <Card padding="lg" className="space-y-3">
        <div className="flex items-center gap-2">
          <MapPin className="size-4 text-muted-foreground" aria-hidden />
          <CardTitle>Detail-rapport per dossier</CardTitle>
        </div>
        <Body tone="muted">
          Onderstaande tabel kan worden geëxporteerd via de knop bovenaan voor verdere analyse in
          Excel.
        </Body>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/60 text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-3 py-2 text-left">Dossier</th>
                <th className="px-3 py-2 text-left">Titel</th>
                <th className="px-3 py-2 text-left">Type</th>
                <th className="px-3 py-2 text-left">Stadsdeel</th>
                <th className="px-3 py-2 text-right">Duur</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {data.reportRows.map((r) => (
                <tr key={r.caseNumber} className="transition-colors hover:bg-muted/40">
                  <td className="px-3 py-2 font-semibold">#{r.caseNumber}</td>
                  <td className="px-3 py-2">{r.title}</td>
                  <td className="px-3 py-2 text-muted-foreground">{r.type}</td>
                  <td className="px-3 py-2 text-muted-foreground">{r.district}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{r.durationDays} d</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

type UtilizationBarProps = {
  pct: number;
};

function UtilizationBar({ pct }: UtilizationBarProps): React.JSX.Element {
  const clamped = Math.max(0, Math.min(100, pct));
  const tone =
    clamped >= 75
      ? "bg-success-600"
      : clamped >= 40
        ? "bg-primary-600"
        : clamped > 0
          ? "bg-warning-500"
          : "bg-neutral-300";
  return (
    <div className="flex items-center justify-end gap-2">
      <div className="h-2 w-24 overflow-hidden rounded-full bg-muted">
        <div
          className={cn("h-full rounded-full transition-all", tone)}
          style={{ width: `${clamped}%` }}
        />
      </div>
      <span className="w-10 text-right">{pct}%</span>
    </div>
  );
}
